import type { ReactNode } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BookOpenText, Database, FileText, Gavel, Globe, Scale, ShieldAlert, Wrench, type LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { microcopy, useDocumentLocale, type UiLocale } from "../lib/microcopy";
import { HoverCard, HoverCardContent, HoverCardTrigger, Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./overlays";

export interface CitationLike {
  id: string;
  /** The source's marker number (`[S3]` is 3), when it differs from the citation's position in the list. */
  index?: number | null;
  /** A foreign authority's decision is a "ComparativeReference", never a "Precedent" (condition 4.4). */
  sourceType: "Regulation" | "Document" | "DataBank" | "Precedent" | "ComparativeReference" | "Tool";
  sourceId: string;
  title: string;
  locator?: string | null;
  excerpt?: string | null;
  page?: number | null;
  supported?: boolean | null;
  /** For a legal reference: whether the legal corpus holds the article it cites (false = not found, EC-12.02). */
  foundInCorpus?: boolean | null;
  /**
   * A value's or a page's provenance (the statistics catalogue, the open web; 22 Oct 2026): the page a person opens, the publisher, the
   * period, the publisher's update and ECONIC's last check (yyyy-MM-dd). Older answers carry none.
   */
  url?: string | null;
  publisher?: string | null;
  period?: string | null;
  published?: string | null;
  checked?: string | null;
  /** What a statistics value opens in the Knowledge Bank's Statistics tab: its series key (`series:…` or bare) or `dataset:…`. */
  documentKey?: string | null;
}

const sourceIcon: Record<CitationLike["sourceType"], LucideIcon> = { Regulation: Scale, Document: FileText, DataBank: Database, Precedent: Gavel, ComparativeReference: Globe, Tool: Wrench };
const sourceLabel = { Regulation: "sourceRegulation", Document: "sourceDocument", DataBank: "sourceDataBank", Precedent: "sourcePrecedent", ComparativeReference: "sourceComparativeReference", Tool: "sourceTool" } as const;

/** "22 Oct 2026" («22 أكتوبر 2026») from yyyy-MM-dd, Western digits in both languages; the text as given otherwise. */
export function citationDate(date: string | null | undefined, locale: UiLocale = "en"): string | null {
  const d = (date ?? "").trim();
  if (!d) return null;
  if (!/^\d{4}-\d{2}-\d{2}/.test(d)) return d;
  const at = new Date(`${d.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(at.getTime())) return d;
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-AE-u-nu-latn" : "en-GB", { day: "numeric", month: locale === "ar" ? "long" : "short", year: "numeric", timeZone: "UTC" }).format(at);
}

/** The dates a statistics value or a web page carries: "Source updated 8 Oct 2026 · Checked 22 Oct 2026"; null when it has none. */
export function citationDates(citation: Pick<CitationLike, "published" | "checked">, locale: UiLocale = "en"): string | null {
  const parts = [
    citation.published ? microcopy("citationUpdated", locale, { date: citationDate(citation.published, locale) ?? citation.published }) : null,
    citation.checked ? microcopy("citationChecked", locale, { date: citationDate(citation.checked, locale) ?? citation.checked }) : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

/** The page a citation opens outside the platform (http or https only). */
export const citationUrl = (citation: Pick<CitationLike, "url">): string | null => (citation.url && /^https?:\/\//i.test(citation.url.trim()) ? citation.url.trim() : null);

export function citationIcon(sourceType: CitationLike["sourceType"]): LucideIcon {
  // A web page (the open web, 21 Oct 2026) arrives as "Web", outside the typed kinds.
  return (sourceType as string) === "Web" ? Globe : sourceIcon[sourceType] ?? FileText;
}

/** The kind of source in the reader's language ("Statistics", "Legislation"…). */
export function citationKind(sourceType: CitationLike["sourceType"], locale: UiLocale = "en"): string {
  return microcopy((sourceType as string) === "Web" ? "sourceWeb" : sourceLabel[sourceType] ?? "sourceDocument", locale);
}

/** What a citation says, previewed where it is cited: the source, where in it, the excerpt and the citation check's verdict. */
export function CitationPreview({ citation, openable }: { citation: CitationLike; openable?: boolean }) {
  const locale = useDocumentLocale();
  const Icon = citationIcon(citation.sourceType);
  const where = [citation.locator, citation.page ? microcopy("citationPage", locale, { page: citation.page }) : null].filter(Boolean).join(" · ");
  return (
    <div className="space-y-2">
      <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-aegold-700"><Icon className="size-3" /> {citationKind(citation.sourceType, locale)}</p>
      <div>
        <p className="text-sm font-medium leading-snug text-aeblack-900" dir="auto">{citation.title}</p>
        {where ? <p className="text-xs text-muted-foreground" dir="auto">{where}</p> : null}
        {citationDates(citation, locale) ? <p className="text-xs text-muted-foreground" dir="auto">{citationDates(citation, locale)}</p> : null}
      </div>
      {citation.excerpt
        ? <blockquote className="line-clamp-6 border-s-2 border-aegold-400 ps-2.5 text-xs leading-relaxed text-aeblack-800" dir="auto">{citation.excerpt}</blockquote>
        : <p className="text-xs text-muted-foreground">{microcopy("citationNoExcerpt", locale)}</p>}
      {citation.supported === false ? <p className="flex items-start gap-1.5 text-xs text-danger-700"><ShieldAlert className="mt-0.5 size-3.5 shrink-0" /> {microcopy("citationUnverified", locale)}</p> : null}
      {citation.foundInCorpus === false ? <p className="flex items-start gap-1.5 text-xs text-danger-700"><ShieldAlert className="mt-0.5 size-3.5 shrink-0" /> {microcopy("citationNotInCorpus", locale)}</p> : null}
      {openable ? <p className="text-[11px] text-muted-foreground">{microcopy("citationOpen", locale)}</p> : null}
    </div>
  );
}

/** Previews the citation on hover or focus; the trigger keeps its click (which opens the source in full). */
export function CitationHoverCard({ citation, openable, children }: { citation: CitationLike; openable?: boolean; children: ReactNode }) {
  return (
    <HoverCard>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent><CitationPreview citation={citation} openable={openable} /></HoverCardContent>
    </HoverCard>
  );
}

export function CitationChip({ citation, index, onOpen, className }: { citation: CitationLike; index?: number; onOpen?: (c: CitationLike) => void; className?: string }) {
  const Icon = citationIcon(citation.sourceType);
  return (
    <CitationHoverCard citation={citation} openable={!!onOpen}>
      <button
        type="button"
        onClick={() => onOpen?.(citation)}
        className={cn(
          "inline-flex max-w-full items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium leading-5 transition-colors",
          citation.supported === false || citation.foundInCorpus === false ? "border-danger-200 bg-danger-50 text-danger-700 hover:bg-danger-100" : "border-aegold-200 bg-aegold-50 text-aegold-800 hover:bg-aegold-100",
          className,
        )}
      >
        {index !== undefined ? <span className="grid size-4 place-items-center rounded-full bg-aegold-600 text-[9px] text-white">{index}</span> : <Icon className="size-3" />}
        <span className="truncate">{citation.locator ? `${citation.title} · ${citation.locator}` : citation.title}</span>
        {citation.supported === false ? <ShieldAlert className="size-3" /> : null}
      </button>
    </CitationHoverCard>
  );
}

/** An in-text source marker (`[S3]` shows as 3) that previews its source on hover and opens it on click. */
export function CitationMarker({ n, citation, onOpen }: { n: number; citation?: CitationLike; onOpen?: (c: CitationLike) => void }) {
  if (!citation) return <sup className="mx-0.5 text-[10px] font-semibold text-muted-foreground">[{n}]</sup>;
  return (
    <CitationHoverCard citation={citation} openable={!!onOpen}>
      <button
        type="button"
        onClick={() => onOpen?.(citation)}
        aria-label={citation.title}
        className={cn(
          "mx-0.5 inline-grid h-4 min-w-4 place-items-center rounded-full px-1 align-super text-[10px] font-semibold leading-none transition-colors",
          citation.supported === false ? "bg-danger-100 text-danger-700 hover:bg-danger-200" : "bg-aegold-100 text-aegold-800 hover:bg-aegold-200",
        )}
      >
        {n}
      </button>
    </CitationHoverCard>
  );
}

export function CitationList({ citations, onOpen, className }: { citations: CitationLike[]; onOpen?: (c: CitationLike) => void; className?: string }) {
  if (citations.length === 0) return null;
  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      {citations.map((c, i) => (
        <CitationChip key={c.id} citation={c} index={i + 1} onOpen={onOpen} />
      ))}
    </div>
  );
}

export function SourceDrawer({ citation, onClose, extra }: { citation: CitationLike | null; onClose: () => void; extra?: ReactNode }) {
  const locale = useDocumentLocale();
  const Icon = citation ? citationIcon(citation.sourceType) : BookOpenText;
  return (
    <Sheet open={!!citation} onOpenChange={(o) => !o && onClose()}>
      <SheetContent width="max-w-lg">
        <SheetHeader>
          <div className="flex items-center gap-2 text-aegold-700">
            <Icon className="size-4" />
            <span className="text-xs font-semibold uppercase tracking-widest">{citation ? citationKind(citation.sourceType, locale) : null}</span>
          </div>
          <SheetTitle dir="auto">{citation?.title}</SheetTitle>
          {citation?.locator ? <SheetDescription dir="auto">{citation.locator}{citation.page ? ` · ${microcopy("citationPage", locale, { page: citation.page })}` : ""}</SheetDescription> : null}
          {citation && citationDates(citation, locale) ? <SheetDescription dir="auto">{citationDates(citation, locale)}</SheetDescription> : null}
        </SheetHeader>
        <SheetBody>
          {citation?.supported === false || citation?.foundInCorpus === false ? (
            <div className="mb-4 flex items-start gap-2 rounded-xl border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
              <ShieldAlert className="mt-0.5 size-4 shrink-0" />
              {microcopy(citation?.foundInCorpus === false ? "citationNotInCorpus" : "citationUnverified", locale)}
            </div>
          ) : null}
          {citation?.excerpt ? (
            <blockquote dir="auto" className="rounded-xl border-s-4 border-aegold-500 bg-aegold-50/60 p-4 text-sm leading-relaxed text-aeblack-900 [&_mark]:bg-aegold-200 [&_mark]:rounded">
              <Markdown remarkPlugins={[remarkGfm]}>{citation.excerpt}</Markdown>
            </blockquote>
          ) : (
            <p className="text-sm text-muted-foreground">{microcopy("citationNoExcerpt", locale)}</p>
          )}
          {citation && citationUrl(citation) ? (
            <p className="mt-3 text-sm">
              <a className="inline-flex items-center gap-1 text-aegold-700 underline-offset-2 hover:underline" href={citationUrl(citation)!} target="_blank" rel="noopener noreferrer" dir="auto">
                <Globe className="size-3.5" /> {microcopy("citationSite", locale)}
              </a>
            </p>
          ) : null}
          {extra}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
